import { ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { AlertCircle, Lock } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "./alert";

interface PermissionGateProps {
  /** Single permission required */
  permission?: string;
  /** Multiple permissions - user must have ANY of these */
  anyOf?: string[];
  /** Multiple permissions - user must have ALL of these */
  allOf?: string[];
  /** Content to render when permission check passes */
  children: ReactNode;
  /**
   * What to show when permission is denied:
   * - "hide" (default): Render nothing
   * - "message": Show a permission denied message
   * - ReactNode: Custom fallback content
   */
  fallback?: "hide" | "message" | ReactNode;
  /** Custom message for the permission denied alert */
  deniedMessage?: string;
  /** Show loading skeleton while permissions are being fetched */
  showLoadingSkeleton?: boolean;
}

/**
 * Gate content behind permission checks.
 *
 * @example
 * // Hide content if no permission
 * <PermissionGate permission="reports:financial">
 *   <FinancialDashboard />
 * </PermissionGate>
 *
 * @example
 * // Show access denied message
 * <PermissionGate permission="admin:manage" fallback="message">
 *   <AdminPanel />
 * </PermissionGate>
 *
 * @example
 * // Custom fallback
 * <PermissionGate
 *   permission="commissions:view"
 *   fallback={<p>Contact admin for access</p>}
 * >
 *   <CommissionsTable />
 * </PermissionGate>
 *
 * @example
 * // Require multiple permissions
 * <PermissionGate allOf={["reports:view", "reports:export"]}>
 *   <ExportableReport />
 * </PermissionGate>
 */
export function PermissionGate({
  permission,
  anyOf,
  allOf,
  children,
  fallback = "hide",
  deniedMessage = "You don't have permission to view this content.",
  showLoadingSkeleton = false,
}: PermissionGateProps) {
  const { can, canAny, canAll, isLoading } = usePermissions();

  // Handle loading state
  if (isLoading) {
    if (showLoadingSkeleton) {
      return (
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-muted rounded w-3/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-32 bg-muted rounded"></div>
        </div>
      );
    }
    return null;
  }

  // Determine if user has required permission
  let hasAccess = true;
  if (permission) {
    hasAccess = can(permission);
  } else if (anyOf && anyOf.length > 0) {
    hasAccess = canAny(anyOf);
  } else if (allOf && allOf.length > 0) {
    hasAccess = canAll(allOf);
  }

  // User has access, render children
  if (hasAccess) {
    return <>{children}</>;
  }

  // Handle no access
  if (fallback === "hide") {
    return null;
  }

  if (fallback === "message") {
    return (
      <Alert variant="destructive" className="max-w-md">
        <Lock className="h-4 w-4" />
        <AlertTitle>Access Denied</AlertTitle>
        <AlertDescription>{deniedMessage}</AlertDescription>
      </Alert>
    );
  }

  // Custom fallback
  return <>{fallback}</>;
}

/**
 * Convenience component for showing content only when user LACKS a permission.
 * Useful for showing "upgrade" or "contact admin" messages.
 *
 * @example
 * <PermissionDenied permission="premium:features">
 *   <UpgradePrompt />
 * </PermissionDenied>
 */
export function PermissionDenied({
  permission,
  anyOf,
  allOf,
  children,
}: Omit<PermissionGateProps, "fallback" | "deniedMessage" | "showLoadingSkeleton">) {
  const { can, canAny, canAll, isLoading } = usePermissions();

  if (isLoading) {
    return null;
  }

  let hasAccess = true;
  if (permission) {
    hasAccess = can(permission);
  } else if (anyOf && anyOf.length > 0) {
    hasAccess = canAny(anyOf);
  } else if (allOf && allOf.length > 0) {
    hasAccess = canAll(allOf);
  }

  // Inverse: render if user does NOT have access
  return !hasAccess ? <>{children}</> : null;
}
